import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, errorMessage } from './api';
import { isIos, isStandalone } from './platform';

/**
 * Web Push of the installable app (PLAN.md P-16, D-05): the service worker (`/sw.js`) receives the
 * pushes, the subscription is registered with `POST /push/subscriptions`. On iOS pushes exist only for
 * the app installed to the home screen.
 */

export type PushState =
  /** Not decided yet (permission and subscription are being read). */
  | 'checking'
  /** The browser has no push support, or iOS without the installed app. */
  | 'unsupported'
  /** The server has no VAPID key pair: the channel is off for everyone. */
  | 'unavailable'
  /** The user refused notifications in the browser's own dialog (EM7). */
  | 'denied'
  /** Supported and allowed, but this device is not subscribed yet. */
  | 'ready'
  | 'subscribed';

export interface PushControl {
  state: PushState;
  error: string | null;
  pending: boolean;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
}

function supported(): boolean {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window))
    return false;
  if (isIos() && !isStandalone()) return false;
  return true;
}

function keyToBytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

function deviceName(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Browser';
  const device = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Mac/.test(ua)
          ? 'Mac'
          : /Windows/.test(ua)
            ? 'Windows'
            : /Linux/.test(ua)
              ? 'Linux'
              : 'Gerät';
  return `${device} · ${browser}`.slice(0, 200);
}

export function registerServiceWorker(): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((error: unknown) => {
      console.warn('service worker registration failed', error);
    });
  });
}

export function usePush(): PushControl {
  const vapid = useQuery({
    queryKey: ['push', 'vapid'],
    queryFn: () => api<{ publicKey: string }>('/push/vapid'),
    staleTime: Infinity,
    retry: false,
  });
  const [state, setState] = useState<PushState>('checking');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const refresh = useCallback(async () => {
    if (!supported()) {
      setState('unsupported');
      return;
    }
    if (vapid.isError || (vapid.data && vapid.data.publicKey === '')) {
      setState('unavailable');
      return;
    }
    if (!vapid.data) return;
    if (Notification.permission === 'denied') {
      setState('denied');
      return;
    }
    try {
      const registration = await navigator.serviceWorker.getRegistration('/');
      const subscription = await registration?.pushManager.getSubscription();
      setState(subscription ? 'subscribed' : 'ready');
    } catch {
      setState('ready');
    }
  }, [vapid.data, vapid.isError]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    if (!vapid.data) return;
    setPending(true);
    setError(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'denied' : 'ready');
        return;
      }
      const registration = await navigator.serviceWorker.ready;
      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: keyToBytes(vapid.data.publicKey),
        }));
      const json = subscription.toJSON();
      const keys = json.keys ?? {};
      await api<void>('/push/subscriptions', {
        method: 'POST',
        body: {
          endpoint: subscription.endpoint,
          keys: { p256dh: keys['p256dh'] ?? '', auth: keys['auth'] ?? '' },
          device: deviceName(),
        },
      });
      setState('subscribed');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }, [vapid.data]);

  const disable = useCallback(async () => {
    setPending(true);
    setError(null);
    try {
      const registration = await navigator.serviceWorker.getRegistration('/');
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        await api<void>('/push/subscriptions', {
          method: 'DELETE',
          body: { endpoint: subscription.endpoint },
        });
        await subscription.unsubscribe();
      }
      setState('ready');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }, []);

  return { state, error, pending, enable, disable };
}
